/**
 * Competitor signup blocklist — shared between client and server.
 *
 * Two-pronged match (both are checked; any hit blocks):
 *   1) ROOT_NAMES  — substring tokens. Kept narrow to specific brand names
 *                    that are unlikely to collide with unrelated domains
 *                    ("sprout" alone would false-positive, so we use
 *                    "sproutsocial" instead).
 *   2) EXACT_DOMAINS — literal domain matches. Catches short-link domains
 *                    (buff.ly, ow.ly) and broad-brand cases where a
 *                    substring would be too loose (zoho.com).
 *
 * Consumers: both client SignupModal and server /api/auth/signup route.
 * Keep this file CommonJS so `require()` works from the Express backend;
 * webpack/CRA will auto-interop for the React import.
 */

const ROOT_NAMES = [
  'buffer',
  'hootsuite',
  'socialbee',
  'sproutsocial',
  'canva',
  'planoly',
  'loomly',
  'agorapulse',
  'meetedgar',
  'metricool',
  'publer',
  'postplanner',
  'sendible',
  'coschedule',
  'sprinklr',
  'socialpilot',
  'contentstudio',
  'brandwatch',
];

const EXACT_DOMAINS = [
  // Buffer
  'buffer.com',
  'buff.ly',
  // Hootsuite
  'hootsuite.com',
  'hsuite.co',
  'ow.ly',
  // Later
  'later.com',
  'latermedia.com',
  // SocialBee
  'socialbee.io',
  'socialbee.com',
  // Sprout Social
  'sproutsocial.com',
  'sprout.social',
  // Canva
  'canva.com',
  // Planoly
  'planoly.com',
  // Loomly
  'loomly.com',
  // Agorapulse
  'agorapulse.com',
  // MeetEdgar
  'meetedgar.com',
  // Metricool
  'metricool.com',
  // Publer
  'publer.io',
  'publer.com',
  // Postplanner
  'postplanner.com',
  // Sendible
  'sendible.com',
  // CoSchedule
  'coschedule.com',
  // Sprinklr
  'sprinklr.com',
  // SocialPilot
  'socialpilot.co',
  // ContentStudio
  'contentstudio.io',
  // Zoho (ZohoSocial is a product; exact match only — Zoho has many unrelated products)
  'zoho.com',
  // Brandwatch / Falcon
  'brandwatch.com',
  'falcon.io',
];

const BLOCK_MESSAGE = 'Sorry, no competitors allowed in the site';

/**
 * Normalize any URL or domain-bearing string to a bare lowercase host.
 *   "HTTPS://WWW.Buffer.COM/about?x=1" → "buffer.com"
 *   "user@mail.Buffer.com"             → "mail.buffer.com"
 */
function normalizeDomain(input) {
  if (!input) return '';
  let s = String(input).trim().toLowerCase();
  // If it looks like an email, take the domain side.
  const at = s.indexOf('@');
  if (at !== -1) s = s.slice(at + 1);
  // Strip protocol and www.
  s = s.replace(/^[a-z]+:\/\//, '').replace(/^www\./, '');
  // Cut at first path/query/fragment separator.
  s = s.split(/[/?#]/)[0];
  return s;
}

/**
 * Returns true if the given domain/email/URL belongs to a blocked competitor.
 * Checks both EXACT_DOMAINS (and any subdomain of them) and ROOT_NAMES
 * (substring anywhere in the hostname). Any hit = blocked.
 */
function isCompetitorDomain(input) {
  const host = normalizeDomain(input);
  if (!host) return false;

  // Exact domain OR subdomain of a blocked domain (mail.buffer.com matches buffer.com).
  for (const d of EXACT_DOMAINS) {
    if (host === d || host.endsWith('.' + d)) return true;
  }
  // Root-name substring match anywhere in the host.
  for (const name of ROOT_NAMES) {
    if (host.includes(name)) return true;
  }
  return false;
}

module.exports = {
  ROOT_NAMES,
  EXACT_DOMAINS,
  BLOCK_MESSAGE,
  normalizeDomain,
  isCompetitorDomain,
};
